import { TENANT_CONFIG, TENANT_TYPES } from './tenantConfig';
import { getPrimaryDomain, isLocalHostname } from './domainUtils';

function normalizeBaseUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  // Remove trailing slashes to make joining predictable
  return trimmed.replace(/\/+$/, '');
}

function joinUrl(base, path) {
  const b = normalizeBaseUrl(base);
  const p = String(path || '').trim();
  if (!b) return p || '';
  if (!p) return b;
  return `${b}${p.startsWith('/') ? '' : '/'}${p}`;
}

/**
 * Resolve the customer-portal base URL.
 *
 * Priority:
 * 1) REACT_APP_CUSTOMER_PORTAL_URL (supports full URL or same-domain path like "/customer-portal")
 * 2) Local dev fallback: http://localhost:3001
 * 3) Tenant-based subdomain: https://app.customer.<primary-domain>
 */
export function getCustomerPortalBaseUrl() {
  if (typeof window === 'undefined') return '';

  const env = normalizeBaseUrl(process.env.REACT_APP_CUSTOMER_PORTAL_URL);
  if (env) return env;

  const hostname = String(window.location?.hostname || '').toLowerCase();
  if (isLocalHostname(hostname)) return 'http://localhost:3001';

  const protocol = window.location?.protocol || 'https:';
  const baseDomain = getPrimaryDomain();
  const customerSubdomain = TENANT_CONFIG?.[TENANT_TYPES.CUSTOMER]?.subdomain || 'app.customer';
  return baseDomain ? `${protocol}//${customerSubdomain}.${baseDomain}` : '';
}

/**
 * Build a full customer-portal URL from a pathname + optional query params.
 * Handles both subdomain-based and path-based setups.
 */
export function buildCustomerPortalUrl(pathname = '/', query = undefined) {
  const base = getCustomerPortalBaseUrl();
  const full = joinUrl(base, pathname);

  if (!query || typeof query !== 'object') return full;

  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v);
    if (!s) return;
    params.set(k, s);
  });
  const qs = params.toString();
  return qs ? `${full}?${qs}` : full;
}

